import { Heart, Star, Trash2 } from "lucide-react";

import {
  useFavourites,
  useRemoveFavourite,
} from "@/features/favourites/model/useFavourites";

export function FavouritesPage() {
  const { data, isLoading, error } = useFavourites();
  const remove = useRemoveFavourite();

  if (isLoading)
    return <div className="text-sm text-muted-foreground">Loading…</div>;
  if (error)
    return (
      <div className="text-sm text-destructive">Could not load favourites.</div>
    );

  return (
    <div className="space-y-4">
      <header className="flex items-start gap-3">
        <div className="rounded-md bg-primary-light p-2 text-primary">
          <Heart className="h-5 w-5" />
        </div>
        <div>
          <h2 className="text-lg font-semibold tracking-display-tight text-copy">
            Favourite workers
          </h2>
          <p className="mt-0.5 text-sm text-copy-muted">
            Workers you've added after a great shift. Use this list to quickly
            re-hire — they'll surface as recommended applicants on future
            postings.
          </p>
        </div>
      </header>

      {(!data || data.length === 0) && (
        <div className="rounded-lg border border-border bg-surface p-8 text-center">
          <p className="text-sm font-medium text-copy">No favourites yet.</p>
          <p className="mt-1 text-xs text-copy-muted">
            Open a completed shift and click{" "}
            <span className="font-medium">Add to favourites</span> to start
            building your list.
          </p>
        </div>
      )}

      {data && data.length > 0 && (
        <ul className="divide-y rounded-lg border border-border bg-surface">
          {data.map((fav) => (
            <li
              key={fav.id}
              className="flex items-center gap-4 p-4"
            >
              <div className="h-12 w-12 shrink-0 overflow-hidden rounded-full border border-border bg-surface-sunken">
                {fav.worker_photo_url ? (
                  <img
                    src={fav.worker_photo_url}
                    alt={fav.worker_name}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-[10px] text-copy-subtle">
                    No photo
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="truncate text-sm font-medium text-copy">
                    {fav.worker_name}
                  </p>
                  {fav.worker_rating_avg != null && (
                    <span className="inline-flex items-center gap-0.5 text-xs text-copy-muted">
                      <Star className="h-3 w-3 fill-warning text-warning" />
                      {fav.worker_rating_avg.toFixed(1)}
                    </span>
                  )}
                </div>
                <p className="text-xs text-copy-muted">
                  {fav.worker_shift_count} shifts completed · added{" "}
                  {new Date(fav.created_at).toLocaleDateString()}
                </p>
                {fav.notes && (
                  <p className="mt-1 text-xs text-copy-muted">{fav.notes}</p>
                )}
              </div>
              <button
                type="button"
                onClick={() => remove.mutate(fav.worker_id)}
                disabled={remove.isPending}
                className="shrink-0 rounded-md p-2 text-copy-muted hover:bg-surface-sunken hover:text-destructive disabled:opacity-50"
                aria-label={`Remove ${fav.worker_name} from favourites`}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
